import { createTheme, alpha } from '@mui/material/styles';

export const flyt = {
	background: '#f4f4f6',
	surface: '#ffffff',
	surfaceMuted: '#ebebed',
	surfaceSunken: 'rgba(15, 39, 73, 0.07)',
	surfaceSunkenHover: 'rgba(15, 39, 73, 0.11)',
	text: '#0f2749',
	textMuted: '#5b6a7d',
	accent: '#0f2749',
	accentContrast: '#ffffff',
	accentMuted: '#dde5ef',
	accentSubtle: '#eef3f9',

	border: 'rgba(15, 39, 73, 0.24)',
	borderStrong: 'rgba(15, 39, 73, 0.38)',
	borderControl: '#6f7785',
	borderHover: '#4a5b72',

	actionPrimary: '#0f2749',
	actionUrgent: '#b05a10',
	actionCritical: '#cc4238',
	actionInfo: '#127a9e',
	actionContrast: '#ffffff',
	actionDestructive: '#b45309',

	danger: { signal: '#d9524a', strong: '#ac3a31', surface: '#f8e3df' },
	warning: { signal: '#8f7440', strong: '#6b5938', surface: '#eee8dd' },
	success: { signal: '#57a087', strong: '#174a40', surface: '#def8eb' },
	info: { signal: '#1786b8', strong: '#004896', surface: '#e8f4fb' },

	sidebar: '#0f2749',
	sidebarForeground: '#dde4ee',
	sidebarMuted: '#b4c1d3',
	sidebarActive: '#21406b',
	sidebarHover: '#1a355c',
	sidebarBorder: '#081b34',

	radiusSm: 4,
	radiusMd: 5,
	radiusLg: 10,
	radiusSection: 14,

	alertMaxWidth: '48rem',

	shadowCard: '0 1px 2px rgba(0, 35, 74, 0.05)',
	shadowOverlay:
		'0 1px 3px rgba(0, 35, 74, 0.08), 0 16px 40px -16px rgba(0, 35, 74, 0.24)',

	fontSans: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
	fontDisplay: '"IBM Plex Serif", ui-serif, Georgia, serif',
};

const admentoColors = {
	blaa: {
		main: '#004896',
		80: alpha('#004896', 0.8),
		60: alpha('#004896', 0.6),
		40: alpha('#004896', 0.4),
		20: alpha('#004896', 0.2),
		5: alpha('#004896', 0.05),
	},
	mork: {
		main: '#0f2749',
		80: alpha('#0f2749', 0.8),
		60: alpha('#0f2749', 0.6),
		40: alpha('#0f2749', 0.4),
		20: alpha('#0f2749', 0.2),
		5: alpha('#0f2749', 0.05),
	},
	lys: {
		main: '#27a7de',
		80: alpha('#27a7de', 0.8),
		60: alpha('#27a7de', 0.6),
		40: alpha('#27a7de', 0.4),
		20: alpha('#27a7de', 0.2),
		5: alpha('#27a7de', 0.05),
	},
	sand: {
		main: '#a98d58',
		80: alpha('#a98d58', 0.8),
		60: alpha('#a98d58', 0.6),
		40: alpha('#a98d58', 0.4),
		20: alpha('#a98d58', 0.2),
		5: alpha('#a98d58', 0.05),
	},
	energi: {
		main: '#8ce0f4',
		80: alpha('#8ce0f4', 0.8),
		60: alpha('#8ce0f4', 0.6),
		40: alpha('#8ce0f4', 0.4),
		20: alpha('#8ce0f4', 0.2),
		5: alpha('#8ce0f4', 0.05),
	},
	fjaer: {
		main: '#e4dbcd',
		80: alpha('#e4dbcd', 0.8),
		60: alpha('#e4dbcd', 0.6),
		40: alpha('#e4dbcd', 0.4),
		20: alpha('#e4dbcd', 0.2),
		5: alpha('#e4dbcd', 0.05),
	},
	plomme: {
		main: '#6b1e27',
		80: alpha('#6b1e27', 0.8),
		60: alpha('#6b1e27', 0.6),
		40: alpha('#6b1e27', 0.4),
		20: alpha('#6b1e27', 0.2),
		5: alpha('#6b1e27', 0.05),
	},
	korall: {
		main: '#ec6a63',
		80: alpha('#ec6a63', 0.8),
		60: alpha('#ec6a63', 0.6),
		40: alpha('#ec6a63', 0.4),
		20: alpha('#ec6a63', 0.2),
		5: alpha('#ec6a63', 0.05),
	},
	skog: {
		main: '#174a40',
		80: alpha('#174a40', 0.8),
		60: alpha('#174a40', 0.6),
		40: alpha('#174a40', 0.4),
		20: alpha('#174a40', 0.2),
		5: alpha('#174a40', 0.05),
	},
	eng: {
		main: '#c4f2da',
		80: alpha('#c4f2da', 0.8),
		60: alpha('#c4f2da', 0.6),
		40: alpha('#c4f2da', 0.4),
		20: alpha('#c4f2da', 0.2),
		5: alpha('#c4f2da', 0.05),
	},
};

const secondaryColor = admentoColors.fjaer.main;

const palette = {
	primary: {
		main: flyt.actionPrimary,
		contrastText: flyt.actionContrast,
	},
	secondary: {
		main: secondaryColor,
		contrastText: flyt.text,
	},
	error: {
		main: flyt.danger.strong,
		light: flyt.danger.surface,
		dark: flyt.danger.strong,
		contrastText: flyt.actionContrast,
	},
	warning: {
		main: flyt.warning.strong,
		light: flyt.warning.surface,
		dark: flyt.warning.strong,
		contrastText: flyt.actionContrast,
	},
	success: {
		main: flyt.success.strong,
		light: flyt.success.surface,
		dark: flyt.success.strong,
		contrastText: flyt.actionContrast,
	},
	info: {
		main: flyt.info.strong,
		light: flyt.info.surface,
		dark: flyt.info.strong,
		contrastText: flyt.actionContrast,
	},
	admento: admentoColors,
	flyt,
	action: {
		active: flyt.text,
	},
	background: {
		default: flyt.background,
		white: flyt.surface,
		paper: flyt.surface,
		gray: flyt.background,
		green: flyt.success.surface,
		blue: flyt.info.surface,
		lightGrayBlue: flyt.accentSubtle,
		red: flyt.danger.surface,
		yellow: flyt.warning.surface,
		orange: flyt.warning.surface,
		highlighted: flyt.accentMuted,
	},
	status: {
		success: flyt.success.strong,
		error: flyt.danger.strong,
		warning: flyt.warning.strong,
		subdued: flyt.textMuted,
		light: {
			gray: flyt.surfaceMuted,
			lightGreen: flyt.success.surface,
			mediumGreen: flyt.success.surface,
			green: flyt.success.surface,
			blue: flyt.info.surface,
			orange: flyt.warning.surface,
			yellow: flyt.warning.surface,
			red: flyt.danger.surface,
			black: flyt.surface,
		},
		dark: {
			gray: flyt.borderControl,
			lightGreen: flyt.success.signal,
			mediumGreen: flyt.success.signal,
			green: flyt.success.signal,
			blue: flyt.info.signal,
			orange: flyt.actionUrgent,
			yellow: flyt.warning.signal,
			red: flyt.actionCritical,
			black: flyt.text,
		},
	},
	deadline: {
		overdue: { fill: '#f4c0b9', edge: flyt.actionCritical },
		started: { fill: '#fbe08a', edge: '#a88400' },
		completed: { fill: '#9fdcbc', edge: '#2e8a62' },
	},
	common: {
		black: flyt.text,
		gray: flyt.surfaceMuted,
		darkGray: flyt.textMuted,
		yellow: flyt.warning.signal,
	},
	borderColor: {
		default: flyt.borderControl,
		hover: flyt.borderHover,
	},
	text: {
		primary: flyt.text,
		light: flyt.textMuted,
		secondary: flyt.textMuted,
		heading: flyt.text,
		white: flyt.actionContrast,
	},
	shadows: {
		default: flyt.shadowCard,
	},
};

const typography = {
	fontFamily: flyt.fontSans,
	headingFontFamily: flyt.fontDisplay,
	headingFontWeight: 600,
	useNextVariants: true,
	button: {
		textTransform: 'none',
		fontWeight: 500,
	},
	h1: {
		fontFamily: flyt.fontDisplay,
		fontSize: '1.5rem',
		fontWeight: 600,
		letterSpacing: '-0.015em',
		lineHeight: 1.25,
		color: flyt.text,
	},
	h2: {
		fontFamily: flyt.fontDisplay,
		fontSize: '1.125rem',
		fontWeight: 600,
		letterSpacing: '-0.01em',
		lineHeight: 1.3,
		color: flyt.text,
	},
	h3: {
		fontFamily: flyt.fontSans,
		fontSize: '1rem',
		fontWeight: 600,
		lineHeight: 1.4,
		color: flyt.text,
	},
	h4: {
		fontFamily: flyt.fontSans,
		fontSize: '1rem',
		fontWeight: 600,
		lineHeight: 1.4,
		color: flyt.text,
	},
	h5: {
		fontFamily: flyt.fontSans,
		fontSize: '0.875rem',
		fontWeight: 600,
		lineHeight: 1.4,
		color: flyt.text,
	},
	h6: {
		fontFamily: flyt.fontSans,
		fontSize: '0.875rem',
		fontWeight: 600,
		lineHeight: 1.4,
		color: flyt.text,
	},
};

const components = {
	MuiCssBaseline: {
		styleOverrides: ({ theme }) => ({
			body: {
				backgroundColor: theme.palette.background.default,
				color: theme.palette.text.primary,
				WebkitFontSmoothing: 'antialiased',
				MozOsxFontSmoothing: 'grayscale',
				textRendering: 'optimizeLegibility',
			},
			/* Bare heading tags do not receive MUI typography variant styles. */
			...['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].reduce(
				(styles, tag) => ({
					...styles,
					[tag]: {
						fontFamily: theme.typography[tag].fontFamily,
						fontSize: theme.typography[tag].fontSize,
						fontWeight: theme.typography[tag].fontWeight,
						letterSpacing: theme.typography[tag].letterSpacing,
						lineHeight: theme.typography[tag].lineHeight,
						color: theme.palette.text.heading,
					},
				}),
				{}
			),
			'.box': {
				backgroundColor: theme.palette.background.white,
				borderRadius: `${theme.palette.flyt.radiusSection}px`,
				border: `1px solid ${theme.palette.flyt.border}`,
				boxShadow: 'none',
			},
			'.box.no-shadow': {
				boxShadow: 'none',
			},
		}),
	},
	MuiPaper: {
		defaultProps: {
			elevation: 0,
		},
		styleOverrides: {
			root: ({ theme }) => ({
				'-webkit-print-color-adjust': 'exact !important',
				'print-color-adjust': 'exact !important',
				backgroundImage: 'none',
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
			}),
			outlined: ({ theme }) => ({
				borderColor: theme.palette.flyt.border,
			}),
		},
	},
	MuiCard: {
		defaultProps: {
			elevation: 0,
		},
		styleOverrides: {
			root: ({ theme }) => ({
				border: `1px solid ${theme.palette.flyt.border}`,
				borderRadius: `${theme.palette.flyt.radiusSection}px`,
				boxShadow: 'none',
			}),
		},
	},
	MuiButtonBase: {
		styleOverrides: {
			root: {
				'-webkit-print-color-adjust': 'exact !important',
				'print-color-adjust': 'exact !important',
			},
		},
	},
	MuiButton: {
		defaultProps: {
			disableElevation: true,
		},
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusMd}px`,
				fontWeight: 500,
				letterSpacing: 0,
				boxShadow: 'none',
				'&:hover': { boxShadow: 'none' },
			}),
			sizeSmall: { padding: '6px 12px', fontSize: '0.875rem' },
			sizeMedium: { padding: '8px 16px', fontSize: '0.875rem' },
			sizeLarge: { padding: '8px 16px', fontSize: '0.875rem' },
			outlinedSizeLarge: { padding: '8px 16px', fontSize: '0.875rem' },
			containedSizeLarge: { padding: '8px 16px', fontSize: '0.875rem' },
			textSizeLarge: { padding: '8px 16px', fontSize: '0.875rem' },
			outlined: ({ theme }) => ({
				borderColor: theme.palette.flyt.borderControl,
				color: theme.palette.text.primary,
				backgroundColor: theme.palette.background.white,
				'&:hover': {
					borderColor: theme.palette.flyt.borderHover,
					backgroundColor: theme.palette.flyt.surfaceMuted,
				},
			}),
			text: ({ theme }) => ({
				color: theme.palette.text.primary,
				'&:hover': { backgroundColor: theme.palette.flyt.surfaceMuted },
			}),
		},
	},
	MuiIconButton: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusMd}px`,
				color: theme.palette.text.primary,
				'&:hover': { backgroundColor: theme.palette.flyt.surfaceMuted },
			}),
		},
	},
	MuiInputBase: {
		defaultProps: {
			variant: 'standard',
		},
		styleOverrides: {
			root: ({ theme }) => ({
				backgroundColor: theme.palette.background.white,

				'&$disabled': {
					opacity: 0.6,
				},
			}),
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusMd}px`,
				backgroundColor: theme.palette.background.white,
				'& .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.flyt.borderControl,
				},
				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.flyt.borderHover,
				},
				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.flyt.accent,
					borderWidth: '2px',
				},
			}),
		},
	},
	MuiSelect: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusMd}px`,
			}),
		},
	},
	MuiBackdrop: {
		styleOverrides: {
			root: {
				background: 'rgba(15, 39, 73, 0.30)',
				backgroundColor: 'rgba(15, 39, 73, 0.30)',
			},
		},
	},
	MuiDrawer: {
		styleOverrides: {
			paper: ({ theme }) => ({
				background: theme.palette.background.white,
				backgroundColor: theme.palette.background.white,
				borderRadius: 0,
			}),
		},
	},
	MuiDivider: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderColor: theme.palette.flyt.border,
			}),
		},
	},
	MuiIcon: {
		styleOverrides: {
			root: {
				textAlign: 'center',
			},
		},
	},
	MuiSnackbar: {
		styleOverrides: {
			anchorOriginTopCenter: { top: '12px !important' },
		},
	},
	MuiAccordion: {
		styleOverrides: {
			root: ({ theme }) => ({
				backgroundColor: theme.palette.background.default,
			}),
		},
	},
	MuiAlert: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
				border: '1px solid transparent',
				maxWidth: theme.palette.flyt.alertMaxWidth,
			}),
			standardSuccess: ({ theme }) => ({
				backgroundColor: theme.palette.flyt.success.surface,
				borderColor: theme.palette.flyt.success.strong,
				color: theme.palette.flyt.success.strong,
			}),
			standardError: ({ theme }) => ({
				backgroundColor: theme.palette.flyt.danger.surface,
				borderColor: theme.palette.flyt.danger.strong,
				color: theme.palette.flyt.danger.strong,
			}),
			standardWarning: ({ theme }) => ({
				backgroundColor: theme.palette.flyt.warning.surface,
				borderColor: theme.palette.flyt.warning.strong,
				color: theme.palette.flyt.warning.strong,
			}),
			standardInfo: ({ theme }) => ({
				backgroundColor: theme.palette.flyt.info.surface,
				borderColor: theme.palette.flyt.info.strong,
				color: theme.palette.flyt.info.strong,
			}),
		},
	},
	MuiChip: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusSm}px`,
				fontWeight: 500,
			}),
			outlined: ({ theme }) => ({
				borderColor: theme.palette.flyt.borderControl,
			}),
		},
	},
	MuiTooltip: {
		styleOverrides: {
			tooltip: ({ theme }) => ({
				fontSize: '0.875rem',
				backgroundColor: theme.palette.flyt.text,
				borderRadius: `${theme.palette.flyt.radiusMd}px`,
				boxShadow: theme.palette.flyt.shadowOverlay,
				color: theme.palette.text.white,
				padding: '8px 12px',
				textAlign: 'left',
				lineHeight: '1.5',
			}),
		},
	},
	MuiTab: {
		styleOverrides: {
			root: {
				textTransform: 'none',
				fontWeight: 500,
			},
			textColorPrimary: ({ theme }) => ({
				color: theme.palette.text.secondary,

				'&:hover': {
					color: theme.palette.primary.main,
				},
				'&.Mui-selected': {
					color: theme.palette.primary.main,
				},
			}),
		},
	},
	MuiDialog: {
		styleOverrides: {
			paper: ({ theme }) => ({
				backgroundColor: theme.palette.background.white,
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
				boxShadow: theme.palette.flyt.shadowOverlay,
			}),
		},
	},
	MuiDialogTitle: {
		styleOverrides: {
			root: ({ theme }) => ({
				fontFamily: theme.typography.headingFontFamily,
				fontSize: '1.125rem',
				fontWeight: 600,
				letterSpacing: '-0.01em',
				color: theme.palette.text.heading,
				padding: theme.spacing(3, 7, 1, 3),
			}),
		},
	},
	MuiDialogContent: {
		styleOverrides: {
			root: ({ theme }) => ({
				padding: theme.spacing(1, 3, 3),
				'&.MuiDialogContent-dividers': {
					borderTopColor: theme.palette.flyt.border,
					borderBottomColor: theme.palette.flyt.border,
				},
			}),
		},
	},
	MuiDialogActions: {
		styleOverrides: {
			root: ({ theme }) => ({
				gap: theme.spacing(1),
				padding: theme.spacing(0, 3, 3),
				'& > :not(style) ~ :not(style)': {
					marginLeft: 0,
				},
			}),
		},
	},
	MuiMenu: {
		styleOverrides: {
			paper: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
				boxShadow: theme.palette.flyt.shadowOverlay,
				border: `1px solid ${theme.palette.flyt.border}`,
			}),
		},
	},
	MuiPopover: {
		styleOverrides: {
			paper: ({ theme }) => ({
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
				boxShadow: theme.palette.flyt.shadowOverlay,
			}),
		},
	},
	MuiFormLabel: {
		styleOverrides: {
			root: ({ theme }) => ({
				color: theme.palette.text.primary,

				'&.Mui-focused': {
					color: theme.palette.text.primary,
				},
			}),
		},
	},
	MuiBox: {
		styleOverrides: {
			root: ({ theme }) => ({
				backgroundColor: theme.palette.text.white,
			}),
		},
	},
	MuiTableCell: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderBottomColor: theme.palette.flyt.border,
			}),
			head: ({ theme }) => ({
				fontWeight: '600',
				backgroundColor: theme.palette.flyt.surfaceMuted,
				color: theme.palette.text.primary,
			}),
		},
	},
	MuiDataGrid: {
		styleOverrides: {
			root: ({ theme }) => ({
				backgroundColor: theme.palette.background.white,
				borderRadius: `${theme.palette.flyt.radiusLg}px`,
				border: `1px solid ${theme.palette.flyt.border}`,
				boxShadow: 'none',
			}),
			columnHeaders: ({ theme }) => ({
				backgroundColor: theme.palette.flyt.surfaceMuted,
			}),
		},
	},
};

const zIndex = {
	modal: 1290,
};

let theme = createTheme({
	palette,
	width: {
		small: '400px',
		medium: '700px',
		large: '1000px',
	},
	shape: {
		borderRadius: flyt.radiusMd,
	},
	typography,
	zIndex,
	components,
});

theme = createTheme(theme, {
	palette: {
		admentoBlaa: theme.palette.augmentColor({
			color: {
				main: admentoColors.blaa.main,
			},
			name: 'admentoBlaa',
		}),
		admentoLys: theme.palette.augmentColor({
			color: {
				main: admentoColors.lys.main,
			},
			name: 'admentoLys',
		}),
		admentoMork: theme.palette.augmentColor({
			color: {
				main: admentoColors.mork.main,
			},
			name: 'admentoMork',
		}),
		admentoSand: theme.palette.augmentColor({
			color: {
				main: admentoColors.sand.main,
			},
			name: 'admentoSand',
		}),
		admentoEnergi: theme.palette.augmentColor({
			color: {
				main: admentoColors.energi.main,
			},
			name: 'admentoEnergi',
		}),
		admentoFjaer: theme.palette.augmentColor({
			color: {
				main: admentoColors.fjaer.main,
			},
			name: 'admentoFjaer',
		}),
		admentoPlomme: theme.palette.augmentColor({
			color: {
				main: admentoColors.plomme.main,
			},
			name: 'admentoPlomme',
		}),
		admentoKorall: theme.palette.augmentColor({
			color: {
				main: admentoColors.korall.main,
			},
			name: 'admentoKorall',
		}),
		admentoSkog: theme.palette.augmentColor({
			color: {
				main: admentoColors.skog.main,
			},
			name: 'admentoSkog',
		}),
		admentoEng: theme.palette.augmentColor({
			color: {
				main: admentoColors.eng.main,
			},
			name: 'admentoEng',
		}),
		actionUrgent: theme.palette.augmentColor({
			color: { main: flyt.actionUrgent },
			name: 'actionUrgent',
		}),
		actionCritical: theme.palette.augmentColor({
			color: { main: flyt.actionCritical },
			name: 'actionCritical',
		}),
		actionInfo: theme.palette.augmentColor({
			color: { main: flyt.actionInfo },
			name: 'actionInfo',
		}),
		actionDestructive: theme.palette.augmentColor({
			color: { main: flyt.actionDestructive },
			name: 'actionDestructive',
		}),
	},
});

export default theme;
